import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import CustomModal from "../../packages/CustomModal";
import DPCard from "../../packages/DPCard";
import {
  validationRangeHardError,
  WPRangedInput,
} from "../../packages/WPInput/WPRangedInput";
import WPTextArea from "../../packages/WPTextArea";

import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../components/Notifications";
import CIPSequence from "../../pages/defaultCIPInformation/CIPSequence";
import {
  getCIPSequenceIdFromSequenceTextArr,
  getCIPSequenceTextArrFromCIPSequenceID,
  normalizeAdditiveProcessFieldsForSave,
} from "../../pages/defaultCIPInformation/cipSequence.utils";
import { DatePickerConfig } from "../DatePopover/DateTimeConfig";
import DupontDatePicker from "../DatePopover/DupontDatePicker";
import DupontRadio from "../DupontRadio";
import {
  ADD_INFO,
  DURATION_CONFIG,
  formatEventTypeLabel,
} from "./CIPEvent.config";

import {
  CIP_EVENT_TYPE_KEYS,
  CIP_EVENT_TYPES,
  CIP_SOURCE_OPTIONS,
  CustomModalButtons,
} from "../../constants/global.constants";
import { CIP_MISSING_VALIDATION } from "../../constants/global.constants";

import { ValueStrings } from "../../utils/constantsString";
import {
  convertMomentToDate,
  formatDate,
  initializeDate,
} from "../../utils/Utils";

import {
  editCip,
  getadvanceSettings,
  getPredictLimits,
  uploadCips,
  uploadRecurrentCips,
} from "../../network/cipAPI";

import "./CIPEventDialog.scss";

const CIPEventDialog = ({
  editedRow,
  onModalClose,
  showLoader,
  refreshTable,
  source,
}) => {
  const { t } = useTranslation();

  const {
    cip_sequence,
    alkali_chem_id,
    alkali_price,
    alkali_used_per_cip,
    alkali_cip_ph,
    alkali_cip_temp,
    alkali_cip_duration,
    additive_alkali_chem_id,
    additive_alkali_price,
    additive_alkali_used_per_cip,
    additive_alkali_ph,
    additive_alkali_temp,
    additive_alkali_duration,
    acid_chem_id,
    acid_price,
    acid_used_per_cip,
    acid_cip_ph,
    acid_cip_temp,
    acid_cip_duration,
    additive_acid_chem_id,
    additive_acid_price,
    additive_acid_used_per_cip,
    additive_acid_ph,
    additive_acid_temp,
    additive_acid_duration,
    add_info,
    selTrain: selectedTrain,
    minDaysBetweenCIPs,
    maxDaysBetweenCIPs,
  } = useSelector((state) => state);

  const [startDate, setStartDate] = useState(() =>
    editedRow ? initializeDate(editedRow.start_date) : null,
  );
  const [endDate, setEndDate] = useState(() =>
    editedRow ? initializeDate(editedRow.end_date) : null,
  );
  const [type, setType] = useState(
    editedRow && editedRow.type ? editedRow.type : "C",
  );
  const [daysBetweenCIPsRange, setDaysBetweenCIPsRange] = useState({
    min: minDaysBetweenCIPs,
    max: maxDaysBetweenCIPs,
  });
  const eventType = CIP_EVENT_TYPES[type];
  const eventTypeLabel = formatEventTypeLabel(eventType);
  const isRecurrentCreate =
    source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow;
  // Consider Shutdown with CIP as shutdown for API calls
  const isShutdown =
    type === CIP_EVENT_TYPE_KEYS.S ||
    type === CIP_EVENT_TYPE_KEYS.SHUTDOWN_WITH_CIP;

  const { duration_per_event, duration_between_event } = DURATION_CONFIG(
    t,
    CIP_EVENT_TYPES[type],
    daysBetweenCIPsRange,
  );

  const storeDefaultCipValues = {
    cip_sequence,
    alkali_chem_id,
    alkali_price,
    alkali_used_per_cip,
    alkali_cip_ph,
    alkali_cip_temp,
    alkali_cip_duration,
    additive_alkali_chem_id,
    additive_alkali_price,
    additive_alkali_used_per_cip,
    additive_alkali_ph,
    additive_alkali_temp,
    additive_alkali_duration,
    acid_chem_id,
    acid_price,
    acid_used_per_cip,
    acid_cip_ph,
    acid_cip_temp,
    acid_cip_duration,
    additive_acid_chem_id,
    additive_acid_price,
    additive_acid_used_per_cip,
    additive_acid_ph,
    additive_acid_temp,
    additive_acid_duration,
    add_info,
  };

  const setCipModalInitialValues = () => ({
    type: "C",
    ...storeDefaultCipValues,
    duration_per_event: duration_per_event.default,
    duration_between_event: duration_between_event.default,
  });

  const [cipModal, setCipModal] = useState(
    editedRow || setCipModalInitialValues(),
  );
  const [cipSequenceTextArr, setCIPSequenceTextArr] = useState(() => {
    const sequenceId = editedRow?.cip_sequence ?? cip_sequence;

    return getCIPSequenceTextArrFromCIPSequenceID(sequenceId);
  });

  const totalSelectedSoakDuration = useMemo(() => {
    const steps = cipSequenceTextArr || [];
    const hasAcidStep =
      steps.includes("acid") || steps.includes("acid solution");
    const hasAlkaliStep =
      steps.includes("alkali") || steps.includes("alkali solution");

    const acidDuration = hasAcidStep ? Number(cipModal?.acid_cip_duration) : 0;
    const alkaliDuration = hasAlkaliStep
      ? Number(cipModal?.alkali_cip_duration)
      : 0;
    const total = (acidDuration || 0) + (alkaliDuration || 0);

    return total > 0 ? total : null;
  }, [
    cipModal?.acid_cip_duration,
    cipModal?.alkali_cip_duration,
    cipSequenceTextArr,
  ]);

  const [errors, setErrors] = useState({
    alkali_price: null,
    alkali_used_per_cip: null,
    alkali_cip_ph: null,
    alkali_cip_temp: null,
    alkali_cip_duration: null,
    additive_alkali_price: null,
    additive_alkali_used_per_cip: null,
    additive_alkali_ph: null,
    additive_alkali_temp: null,
    additive_alkali_duration: null,
    acid_price: null,
    acid_used_per_cip: null,
    acid_cip_ph: null,
    acid_cip_temp: null,
    acid_cip_duration: null,
    additive_acid_price: null,
    additive_acid_used_per_cip: null,
    additive_acid_ph: null,
    additive_acid_temp: null,
    additive_acid_duration: null,
    duration_per_event: null,
    duration_between_event: null,
  });

  const [minDateLimit, setMinDateLimit] = useState(null);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [noError, setNoError] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditDataChanged, setIsEditDataChanged] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [hasMissingRequiredCipFields, setHasMissingRequiredCipFields] =
    useState(false);
  const cipModalRef = useRef(cipModal);
  const hasInitializedCipDefaultsRef = useRef(false);

  const { DATE_TIME_FORMAT } = ValueStrings;

  const onClose = ({ target }) => {
    if (target.id === CustomModalButtons.CONFIRM) {
      saveEventHandler();
    } else {
      onModalClose();
    }
  };

  const saveEventHandler = async (forceSubmit = false) => {
    setLoading(true);
    try {
      const normalizedCipModal = normalizeAdditiveProcessFieldsForSave(
        cipModal,
        storeDefaultCipValues,
      );

      const payload = isShutdown
        ? {
            type: CIP_EVENT_TYPE_KEYS.S,
            start_date: normalizedCipModal.start_date,
            end_date: normalizedCipModal.end_date,
            add_info: normalizedCipModal.add_info,
            ...(isRecurrentCreate && {
              duration_per_event: normalizedCipModal.duration_per_event,
              duration_between_event: normalizedCipModal.duration_between_event,
            }),
          }
        : normalizedCipModal;

      const response = await (editedRow
        ? editCip(cipModal.cip_id, payload, selectedTrain)
        : source === CIP_SOURCE_OPTIONS.RECURRENT
          ? uploadRecurrentCips(payload, selectedTrain, forceSubmit ? 1 : 0)
          : uploadCips(payload, selectedTrain));
      if (response) {
        if (response.cip_id || response.status === 200) {
          onModalClose();
          const key = editedRow
            ? "CIPEvent.editedSuccess"
            : source === CIP_SOURCE_OPTIONS.RECURRENT
              ? "CIPEvent.recurrentSuccess"
              : "CIPEvent.addedSuccess";
          createNotification(
            SUCCESS_NOTIFICATION,
            t(key, {
              eventType: eventTypeLabel,
            }),
          );
          // This will show the table (fetch CIP call will be triggered on initial mount, initial recurrent case)
          showLoader && showLoader(true);

          // explicitly fetch the CIPS (this is after paginated table mounted, more recurrent case)
          if (refreshTable) {
            await refreshTable();
          }
        } else {
          throw new Error(response.detail?.error);
        }
      }
    } catch (err) {
      setLoading(false);
      showLoader && showLoader(false);
      const missingCipsErrorMessage = err.message || err.Error || "";
      const isValidationError =
        missingCipsErrorMessage === CIP_MISSING_VALIDATION.ERROR_MESSAGE;

      if (isValidationError && !forceSubmit) {
        setShowValidationWarning(true);
        return;
      }

      const errorMsg = err.message || t("notificationStrings.somethingWrong");
      createNotification(ERROR_NOTIFICATION, errorMsg);
      onModalClose();
    }
  };

  const onStartDateChange = (value) => {
    const isoDate = formatDate(value);
    setStartDate(value);
    setStartDateError("");
    setCipModal((prev) => ({
      ...prev,
      start_date: isoDate,
      end_date: null,
    }));
    setEndDate(null);
    setEndDateError("");
  };

  const onEndDateChange = (value) => {
    if (!value) {
      setEndDate(null);
      setEndDateError("");
      setCipModal((prev) => ({ ...prev, end_date: null }));
      return;
    }

    let newEndDate = new Date(value);
    const minDateTime = getMinDate();
    setEndDateError(""); // Clear previous error

    // Adjust time if it's less than minimum allowed
    if (minDateTime && newEndDate < minDateTime) {
      const isSameDay =
        newEndDate.toDateString() === minDateTime.toDateString();
      if (isSameDay) {
        newEndDate = new Date(minDateTime);
      }
    }

    // Validate against duration if we have start date and duration
    if (isRecurrentCreate && startDate && cipModal.duration_per_event) {
      const minDurationDateTime = calculateMinDateTime();

      if (minDurationDateTime && newEndDate < minDurationDateTime) {
        const formattedDateTime = formatDate(
          minDurationDateTime,
          "DD/MM/YYYY, hh:mm",
        );
        setEndDateError(
          t("CIPEvent.endDateDurationError", {
            minDateTime: formattedDateTime,
          }),
        );

        if (newEndDate.toDateString() === minDurationDateTime.toDateString()) {
          newEndDate = new Date(minDurationDateTime);
        }
      }
    }

    setEndDate(newEndDate);
    setCipModal((prev) => ({ ...prev, end_date: formatDate(newEndDate) }));
  };

  const handleRadioChange = (event) => {
    const { name } = event.target;
    setCipModal((prev) => ({ ...prev, type: name }));
    setType(name);
  };

  const handleInputAddChange = (key, value) => {
    setCipModal((prev) => ({ ...prev, [key]: value }));
  };

  const handleValidationWarningClose = (buttonId) => {
    setShowValidationWarning(false);
    if (buttonId === CustomModalButtons.CANCEL) {
      onModalClose();
      saveEventHandler(true);
    }
  };

  const handleInputChange = (field, value, minValue, maxValue) => {
    const validationError = getFieldValidationError(
      field,
      value,
      minValue,
      maxValue,
    );

    setErrors((prev) => ({ ...prev, [field]: validationError }));
    setCipModal((prev) => ({ ...prev, [field]: value }));

    // Check end date validation when duration changes
    if (
      isRecurrentCreate &&
      field === "duration_per_event" &&
      startDate &&
      endDate
    ) {
      const durationHours = parseFloat(value);
      if (!isNaN(durationHours)) {
        const minEndDateTime = new Date(startDate);
        if (duration_per_event.unit === "days") {
          minEndDateTime.setDate(minEndDateTime.getDate() + durationHours);
        } else {
          const hoursPart = Math.floor(durationHours);
          const minutesPart = Math.round((durationHours - hoursPart) * 60);
          minEndDateTime.setHours(minEndDateTime.getHours() + hoursPart);
          minEndDateTime.setMinutes(minEndDateTime.getMinutes() + minutesPart);
        }

        if (endDate < minEndDateTime) {
          setEndDateError(
            t("CIPEvent.endDateDurationError", {
              duration: `${value} ${duration_per_event.unit}`,
              minDateTime: formatDate(minEndDateTime, "DD/MM/YYYY, hh:mm"),
            }),
          );
        } else {
          setEndDateError("");
        }
      }
    }
  };

  const getFieldValidationError = useCallback(
    (_field, value, minValue, maxValue) => {
      if (minValue === null || minValue === undefined) {
        return null;
      }

      if (maxValue === null || maxValue === undefined) {
        return null;
      }

      const normalizedValue =
        value === null || value === undefined ? "" : String(value).trim();

      if (normalizedValue === "") {
        return t("ValueStrings.requiredField");
      }

      const numericValue = Number(normalizedValue);
      if (Number.isNaN(numericValue)) {
        return t("ValueStrings.requiredField");
      }

      const hardError = validationRangeHardError(t, numericValue, [
        minValue,
        maxValue,
      ]);

      return hardError?.error || null;
    },
    [t],
  );

  const heading = t("CIPEvent.heading", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const headerHelper = t("CIPEvent.description", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const CIPEventHeader = <div className="heading">{heading}</div>;
  const CIPEventHeaderHelper = (
    <div className="header-helper">{headerHelper}</div>
  );
  const hasInvalidRecurrentDuration =
    isRecurrentCreate &&
    (String(cipModal.duration_per_event ?? "").trim() === "" ||
      String(cipModal.duration_between_event ?? "").trim() === "" ||
      !!errors.duration_per_event ||
      !!errors.duration_between_event);

  const startDateLabel = isRecurrentCreate
    ? `${eventTypeLabel} ${t("CIPEvent.seriesStartDate")}`
    : `${eventTypeLabel} ${t("CIPEvent.startDate")}`;
  const endDateLabel = isRecurrentCreate
    ? `${eventTypeLabel} ${t("CIPEvent.seriesEndDate")}`
    : `${eventTypeLabel} ${t("CIPEvent.endDate")}`;

  const fetchDateLimits = async () => {
    try {
      const limits = await getPredictLimits(selectedTrain);
      if (limits.data) {
        setMinDateLimit(convertMomentToDate(limits.data.min_date));
        setMaxDateLimit(convertMomentToDate(limits.data.max_date));
      } else {
        throw new Error("error");
      }
    } catch (err) {
      setLoading(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
      );
      onModalClose();
    }
  };

  const fetchDurationLimits = async () => {
    try {
      const advanceSettings = await getadvanceSettings(selectedTrain);
      if (advanceSettings?.data?.settings) {
        const { min_day_between_cips, max_days_between_cips } =
          advanceSettings.data.settings;

        setDaysBetweenCIPsRange({
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        const durationConfig = DURATION_CONFIG(t, CIP_EVENT_TYPES[type], {
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        setCipModal((prev) => ({
          ...prev,
          duration_between_event: durationConfig.duration_between_event.default,
        }));
      } else {
        throw new Error("error");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
      );
      onModalClose();
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 700);
    }
  };

  // Compare cipModal with editedRow for changes
  const editDataChangedHandler = () => {
    if (!editedRow) return false;

    const keys = Object.keys(cipModal);
    for (let key of keys) {
      if (key === "start_date" || key === "end_date") {
        if (formatDate(editedRow[key]) !== formatDate(cipModal[key])) {
          return true;
        }
      } else if ((editedRow[key] ?? "") !== (cipModal[key] ?? "")) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    cipModalRef.current = cipModal;
  }, [cipModal]);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null,
    );
    setNoError(noError);
  }, [errors]);

  useEffect(() => {
    if (!isRecurrentCreate || type !== CIP_EVENT_TYPE_KEYS.C) {
      return;
    }

    const totalSoakDuration = totalSelectedSoakDuration;
    const durationPerEventValue = cipModal.duration_per_event;
    const rawDurationValue = String(durationPerEventValue ?? "").trim();
    const durationValue = Number(durationPerEventValue);
    const isDurationBelowSoakTotal =
      totalSoakDuration !== null &&
      rawDurationValue !== "" &&
      durationValue < totalSoakDuration;

    if (isDurationBelowSoakTotal) {
      setErrors((prev) => {
        const nextError = t("CIPEvent.durationPerEventMustExceedSoakDuration", {
          minDuration: totalSoakDuration,
        });

        if (prev.duration_per_event === nextError) {
          return prev;
        }

        return {
          ...prev,
          duration_per_event: nextError,
        };
      });
      return;
    }

    setErrors((prev) => {
      if (prev.duration_per_event === null) {
        return prev;
      }

      const hardError = getFieldValidationError(
        "duration_per_event",
        cipModal.duration_per_event,
        duration_per_event.ranges?.hard?.min,
        duration_per_event.ranges?.hard?.max,
      );

      if (prev.duration_per_event === hardError) {
        return prev;
      }

      return {
        ...prev,
        duration_per_event: hardError,
      };
    });
  }, [
    cipModal.duration_per_event,
    duration_per_event.ranges,
    getFieldValidationError,
    totalSelectedSoakDuration,
    isRecurrentCreate,
    t,
    type,
  ]);

  useEffect(() => {
    setLoading(true);
    if (source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow) {
      Promise.all([fetchDateLimits(), fetchDurationLimits()]);
    } else {
      fetchDateLimits().finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (
      type !== CIP_EVENT_TYPE_KEYS.C ||
      hasInitializedCipDefaultsRef.current
    ) {
      return;
    }

    hasInitializedCipDefaultsRef.current = true;

    if (editedRow?.type === CIP_EVENT_TYPE_KEYS.C) {
      return;
    }

    const nextCipModal = {
      ...cipModalRef.current,
      ...storeDefaultCipValues,
      type: CIP_EVENT_TYPE_KEYS.C,
    };

    setCipModal(nextCipModal);
    setErrors((previousErrors) =>
      Object.keys(previousErrors).reduce(
        (nextErrors, field) => ({
          ...nextErrors,
          [field]:
            field.startsWith("duration_") ||
            String(nextCipModal[field] ?? "").trim() !== ""
              ? null
              : t("ValueStrings.requiredField"),
        }),
        {},
      ),
    );
  }, [
    editedRow,
    type,
    cip_sequence,
    alkali_chem_id,
    alkali_price,
    alkali_used_per_cip,
    alkali_cip_ph,
    alkali_cip_temp,
    alkali_cip_duration,
    additive_alkali_chem_id,
    additive_alkali_price,
    additive_alkali_used_per_cip,
    additive_alkali_ph,
    additive_alkali_temp,
    additive_alkali_duration,
    acid_chem_id,
    acid_price,
    acid_used_per_cip,
    acid_cip_ph,
    acid_cip_temp,
    acid_cip_duration,
    additive_acid_chem_id,
    additive_acid_price,
    additive_acid_used_per_cip,
    additive_acid_ph,
    additive_acid_temp,
    additive_acid_duration,
    add_info,
  ]);

  useEffect(() => {
    if (type !== CIP_EVENT_TYPE_KEYS.C) {
      hasInitializedCipDefaultsRef.current = false;
    }
  }, [type]);

  useEffect(() => {
    if (editedRow) {
      setIsEditDataChanged(editDataChangedHandler());
    }
  }, [cipModal, editedRow]);

  useEffect(() => {
    if (!editedRow) {
      if (type === CIP_EVENT_TYPE_KEYS.C) {
        setCipModal({
          ...setCipModalInitialValues(),
          start_date: startDate ? formatDate(startDate) : null,
          end_date: endDate ? formatDate(endDate) : null,
        });
      } else {
        setCipModal({
          type,
          start_date: startDate ? formatDate(startDate) : null,
          end_date: endDate ? formatDate(endDate) : null,
          duration_per_event: duration_per_event.default,
          duration_between_event: duration_between_event.default,
        });
      }
    }
  }, [type]);

  useEffect(() => {
    if (type !== CIP_EVENT_TYPE_KEYS.C) {
      return;
    }

    setCipModal((prev) => ({
      ...prev,
      cip_sequence: getCIPSequenceIdFromSequenceTextArr(cipSequenceTextArr),
    }));
  }, [type, cipSequenceTextArr]);

  const calculateMinDateTime = () => {
    if (!isRecurrentCreate || !startDate || !cipModal.duration_per_event) {
      return null;
    }

    const minDateTime = new Date(startDate);
    const duration = parseFloat(cipModal.duration_per_event);

    if (duration_per_event.unit === "days") {
      minDateTime.setDate(minDateTime.getDate() + duration);
    } else {
      const totalMinutes = duration * 60;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.round(totalMinutes % 60);

      minDateTime.setHours(minDateTime.getHours() + hours);
      minDateTime.setMinutes(minDateTime.getMinutes() + minutes);
    }
    return minDateTime;
  };

  const getMinDate = () => {
    if (!startDate) return startDate;
    return calculateMinDateTime() || startDate;
  };

  const getMinTime = () => {
    const minDateTime = calculateMinDateTime();
    if (!minDateTime) return undefined;

    // Only apply minTime if selected date is the same as calculated minimum date
    const selectedDate = endDate || new Date();
    return selectedDate.toDateString() === minDateTime.toDateString()
      ? minDateTime
      : undefined;
  };

  return (
    <CustomModal
      header={CIPEventHeader}
      headerHelper={CIPEventHeaderHelper}
      confirmBtn={t("CIPEvent.saveEvent")}
      cancelBtn={t("CIPEvent.close")}
      onModalClose={onClose}
      width="1100px"
      customclass="cip-event-dialog"
      confirmBtnDisabled={
        !startDate ||
        startDateError ||
        endDateError ||
        !noError ||
        (editedRow ? !isEditDataChanged : false) ||
        hasInvalidRecurrentDuration ||
        (type === CIP_EVENT_TYPE_KEYS.C && hasMissingRequiredCipFields)
      }
      isLoading={loading}
    >
      <DPCard>
        <div className="cip-event-fields">
          <div>
            <div className="field-name">{t("predictionStrings.type")}</div>
            <div className="radio-container" onChange={handleRadioChange}>
              <DupontRadio
                text={t("CIPEvent.CIP")}
                checked={type === CIP_EVENT_TYPE_KEYS.C}
                onClick={() => setType(CIP_EVENT_TYPE_KEYS.C)}
                name="C"
                value="C"
              />
              <DupontRadio
                text={t("CIPEvent.shutdown")}
                checked={
                  type === CIP_EVENT_TYPE_KEYS.S ||
                  type === CIP_EVENT_TYPE_KEYS.SHUTDOWN_WITH_CIP
                }
                onClick={() => setType(CIP_EVENT_TYPE_KEYS.S)}
                name="S"
                value="S"
              />
            </div>
          </div>
          <div className="vertical-seperator"></div>
          <div className="date-fields-container">
            <DupontDatePicker
              selected={startDate}
              onChange={onStartDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              minDate={minDateLimit}
              maxDate={maxDateLimit}
              label={startDateLabel}
              labelClass={
                type === "S" ? "cip-event-date-label--shutdown" : undefined
              }
              className={"text-left width-fill"}
              error={startDateError}
              isRequired
              placeholderText={t("CIPEvent.selectDateAndTime")}
              {...DatePickerConfig}
            />
            <DupontDatePicker
              selected={endDate}
              onChange={onEndDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              label={endDateLabel}
              labelClass={
                type === "S" ? "cip-event-date-label--shutdown" : undefined
              }
              disabled={!Boolean(startDate)}
              error={endDateError}
              minDate={getMinDate()}
              minTime={getMinTime()}
              className={"width-fill"}
              placeholderText={t("CIPEvent.selectDateAndTime")}
              {...DatePickerConfig}
            />
          </div>

          {isRecurrentCreate && (
            <>
              <div className="vertical-seperator"></div>
              <div className="duration-fields-container">
                {[duration_per_event, duration_between_event].map((field) => (
                  <WPRangedInput
                    key={field.key}
                    value={cipModal[field.key]}
                    name={field.key}
                    label={field.label}
                    suffix={field.unit}
                    externalError={errors?.[field.key]}
                    onChange={(e) =>
                      handleInputChange(
                        field.key,
                        e.target.value,
                        field.ranges?.hard?.min,
                        field.ranges?.hard?.max,
                      )
                    }
                    ranges={field.ranges}
                    maxLength={10}
                    required
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DPCard>
      {type === CIP_EVENT_TYPE_KEYS.C && (
        <div className="marginTop10">
          <CIPSequence
            cipSequenceTextArr={cipSequenceTextArr}
            setCIPSequenceTextArr={setCIPSequenceTextArr}
            setChangedCIPValues={setCipModal}
            errors={errors}
            handleInputChange={handleInputChange}
            onFieldChange={handleInputAddChange}
            onMandatoryValidationChange={setHasMissingRequiredCipFields}
            disabled={false}
            existingCIPState={cipModal}
          />
        </div>
      )}
      <div className="margin-top-10">
        <WPTextArea
          value={cipModal[ADD_INFO.key]}
          label={t(`CIPEvent.addInfo`)}
          rows={ADD_INFO.multiline}
          onChange={(value) => handleInputAddChange(ADD_INFO.key, value)}
          maxCount={ADD_INFO.maxChar}
          placeholder={t(`CIPEvent.addInfoDefaultText`)}
        />
      </div>
      {showValidationWarning && (
        <ConfirmationPopup
          open={showValidationWarning}
          type={DIALOG_TYPES.WARNING}
          header={t("CIPEvent.missingCIPHeader")}
          description={t("CIPEvent.missingCIPDescription")}
          onClose={handleValidationWarningClose}
          cancelBtn={t("CIPEvent.missingCIPCancelBtn")}
          confirmBtn={t("CIPEvent.missingCIPConfirmBtn")}
          customBackdrop={true}
        ></ConfirmationPopup>
      )}
    </CustomModal>
  );
};

export default CIPEventDialog;
